import { authConfig } from '../auth/config'
import { clearTokens, readTokens } from '../auth/storage'

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3000"

export interface Project {
  id: string
  name: string
  slack_channel: string
  status: string
  start_date: string | null
  end_date: string | null
  jira_epic_key: string | null
  manager_slack_id: string
  deliverables: Deliverable[]
  engineers: Engineer[]
}

export interface Deliverable {
  id: string
  name: string
  status: string
  due_date: string | null
}

export interface Engineer {
  id: string
  name: string
  slack_user_id: string
  role: string | null
}

export interface HealthSummary {
  id: string
  name: string
  status: string
  deliverables_total: number
  deliverables_done: number
  deliverables_at_risk: number
}

export interface WorkloadEntry {
  id: string
  name: string
  slack_user_id: string
  active_projects: { id: string; name: string; role: string | null }[]
}

/**
 * Authed fetch wrapper. Attaches the Cognito ID token as a Bearer header
 * when auth is enabled, and treats 401 as "session ended" — clears tokens
 * and forces a full page reload so AuthProvider re-runs the login redirect.
 */
async function fetchJson<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {}
  if (authConfig.enabled) {
    const tokens = readTokens()
    if (tokens) headers['Authorization'] = `Bearer ${tokens.idToken}`
  }

  const res = await fetch(`${API_BASE}${path}`, { headers })

  if (res.status === 401 && authConfig.enabled) {
    clearTokens()
    // Hard reload — AuthProvider on next mount sees no token and redirects
    // to the Hosted UI. window.location ensures sessionStorage is fresh.
    window.location.assign('/')
    throw new Error('Session expired')
  }

  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

export const api = {
  getProjects: () => fetchJson<Project[]>("/api/projects"),
  getActiveProjects: () => fetchJson<Project[]>("/api/projects/active"),
  getProject: (id: string) => fetchJson<Project>(`/api/projects/${id}`),
  getHealth: () => fetchJson<HealthSummary[]>("/api/health"),
  getWorkload: () => fetchJson<WorkloadEntry[]>("/api/workload"),
}
